import { formatIndianNumberWithDecimals } from "../../utils/currency.utils";
import { getLogoUrl } from "../../utils/getLogoUrl";

const normalizeNomineeStatus = (status) => {
    if (!status) return "none";
    const normalized = String(status).trim().replace(/[_-]/g, " ").toLowerCase();

    if (normalized.includes("not registered")) return "not registered";
    if (normalized.includes("registered")) return "registered";

    return "none";
};

const formatAsOnDate = (isoString) => {
    if (!isoString) return "-";
    try {
        const date = new Date(isoString);
        let formatted = date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
            timeZone: "Asia/Kolkata",
        }).replace(",", ";");

        // Ensure AM/PM is uppercase
        formatted = formatted.toUpperCase();

        return formatted + " IST";
    } catch {
        return "-";
    }
};

/**
 * Parser for the new bank accounts API.
 * 
 * @param {Object} data - The result object from the API response
 * @returns {Object} Parsed data for the UI
 */
export const parseBankAccounts = (data) => {
    if (!data) return { bankAccounts: [], totalBalance: "₹0", totalBalanceLabel: "Total Balance", asOfDate: "" };

    const bankAccounts = (data?.bankAccounts || []).map(acc => ({
        id: acc?.id,
        bankName: acc?.bankName || "Unknown Bank",
        accountNumber: acc?.accountId || "XXXXXXXXXXXX",
        nomineeStatus: acc?.nomineeStatus || "Not Registered",
        NomineStatus: normalizeNomineeStatus(acc?.nomineeStatus),
        balance: acc?.currentBalance != null ? `₹${formatIndianNumberWithDecimals(acc?.currentBalance)}` : "-",
        lastUpdated: formatAsOnDate(data?.asOnDate),
        // logo: acc?.bankLogo || null,
        accountType: acc?.accountType || "Savings"
    }));

    return {
        asOfDate: `as on date ${formatAsOnDate(data?.asOnDate)}`,
        totalBalance: data?.totalBalance != null ? `₹${formatIndianNumberWithDecimals(data?.totalBalance)}` : "₹0",
        totalBalanceLabel: "Total Balance",
        bankAccounts
    };
};

/**
 * Parser for the bank account transactions API.
 * 
 * @param {Object} data - The result object from the API response
 * @returns {Object} Parsed transactions and pagination info
 */
export const parseBankTransactions = (data) => {
    if (!data) return { transactions: [], pagination: { total: 0, totalPages: 0, page: 1, limit: 10 } };

    const transactions = (data?.transactions || []).map((txn, index) => {
        const type = String(txn?.type || "DEBIT").toUpperCase();
        const isCredit = type === "CREDIT" || type === "CR" || type === "DEPOSIT";

        // Handle amount formatting: + ₹1,500
        const rawAmount = txn?.amount ?? 0;
        const formattedAmount = `${isCredit ? "+ " : ""}₹${formatIndianNumberWithDecimals(rawAmount)}`;

        // Handle date formatting: 28 January, 2025 • 03:05 pm
        let displayDate = "-";
        if (txn?.date) {
            try {
                const d = new Date(txn.date);
                const datePart = d.toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric"
                });
                const timePart = d.toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true
                }).toLowerCase();
                displayDate = `${datePart} • ${timePart}`;
            } catch {
                displayDate = "-";
            }
        }

        return {
            id: txn?.id || index,
            timestamp: displayDate,
            amount: formattedAmount,
            type: txn?.type ?? "DEBIT",
            title: txn?.description || "Transaction", // Keep description for internal/narrative use
            merchantName: txn?.merchantName || "N/A", // STRICT mapping to merchantName only
            merchantLogo: txn?.merchantLogo || null,
            isCredit,
            rawAmount
        };
    });

    return {
        transactions,
        pagination: {
            total: data?.pagination?.total ?? 0,
            page: data?.pagination?.page ?? 1,
            limit: data?.pagination?.limit ?? 10,
            totalPages: data?.pagination?.totalPages ?? 0
        }
    };
};
